import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { formatDateForDisplay } from '../utils/dateFormat';
import { trackFeatureUsage, trackDownload, trackEvent } from '../utils/analytics';

const PlatformInvoices = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPlatformInvoices();
    trackFeatureUsage('Platform Invoices', 'view');
  }, []);

  const fetchPlatformInvoices = async () => {
    try {
      const response = await axios.get('/api/customers/platform-invoices');
      setInvoices(response.data || []);
    } catch (error) {
      console.error('Failed to fetch platform invoices:', error);
      setError('Failed to load platform invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoiceId, fileName) => {
    try {
      trackDownload(fileName, 'pdf');
      trackEvent('platform_invoice_downloaded', {
        event_category: 'Platform Invoice Management',
        event_label: fileName,
        invoice_id: invoiceId
      });

      const response = await axios.get(`/api/customers/platform-invoices/${invoiceId}/download`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download platform invoice:', error);
      setError('Failed to download platform invoice. Please try again.');
    }
  };

  const getInvoiceStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'var(--success)';
      case 'PENDING': return 'var(--warning)';
      case 'OVERDUE': return 'var(--error)';
      case 'CANCELLED': return 'var(--gray-dark)';
      default: return 'var(--gray-dark)';
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading">Loading platform invoices...</div>
      </div>
    );
  }

  return (
    <div className="page">
      <h1 className="page-title">Platform Invoices</h1>
      
      <div style={{
        marginBottom: '20px',
        padding: '15px',
        background: 'linear-gradient(135deg, #4A90E2 0%, #2C5282 100%)',
        color: 'white',
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '5px' }}>
          📄 Your Platform Subscription Invoices
        </div>
        <div style={{ fontSize: '14px', opacity: '0.9' }}>
          Download and view your CalorieNSweat platform subscription invoices
        </div>
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
            Subscription Invoices ({invoices.length} total)
          </h3>
          <button 
            className="btn btn-secondary"
            onClick={fetchPlatformInvoices}
            disabled={loading}
          >
            🔄 Refresh
          </button>
        </div>

        {invoices.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
            No platform subscription invoices found.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                    Invoice #
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                    Invoice Date
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                    Description
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                    Amount
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                    Status
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                    Payment Date
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--light-blue)' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice, index) => (
                  <tr 
                    key={invoice.id}
                    style={{ 
                      backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                      borderBottom: '1px solid var(--gray-medium)'
                    }}
                  >
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      {invoice.invoiceNumber}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {formatDateForDisplay(invoice.invoiceDate)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div>{invoice.description}</div>
                      <div style={{ fontSize: '11px', color: 'var(--primary-blue)', marginTop: '4px' }}>
                        Platform Subscription
                      </div>
                    </td>
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      ₹{invoice.totalAmount || invoice.amount}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        color: getInvoiceStatusColor(invoice.status),
                        fontWeight: '500'
                      }}>
                        {invoice.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {invoice.paymentDate ? formatDateForDisplay(invoice.paymentDate) : '-'}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {invoice.hasPdf && (
                        <button 
                          className="btn btn-primary"
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                          onClick={() => handleDownloadInvoice(invoice.id, invoice.pdfFileName || `platform_invoice_${invoice.invoiceNumber}.pdf`)}
                          title="Download PDF"
                        >
                          📄 Download
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Information Section */}
        <div style={{
          marginTop: '30px',
          padding: '20px',
          background: 'var(--pale-blue)',
          borderRadius: '8px',
          border: '2px solid var(--primary-blue)'
        }}>
          <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
            📋 About Platform Invoices
          </h4>
          <div style={{ fontSize: '14px', color: 'var(--gray-dark)', lineHeight: '1.6' }}>
            <ul style={{ marginLeft: '20px' }}>
              <li>These are your CalorieNSweat platform subscription invoices</li>
              <li>Monthly subscription fee: ₹1,000 + GST (₹180) = ₹1,180 total</li>
              <li>Invoices are generated automatically upon successful payment</li>
              <li>All invoices are issued by Mind Yards Solutions Private Limited</li>
              <li>For any billing queries, contact support at info@mindyards.com</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformInvoices;
